import {
  customerDisplayName,
  isNameUnsure,
} from "@/features/customers/name-verification";
import type { Customer } from "@/features/customers/types";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/features/utils/i18n";

/**
 * The "?" put right after a customer's name the agent only heard on a call — never spelled,
 * never written. Decide with `isNameUnsure` (a full customer) or the server's
 * `unsure_customer_name` (lists), and render this when it holds.
 *
 * Small and light grey: it qualifies the name, it is not an alert — in orange it read as a
 * warning on half the rows of a list. Its tooltip says what it means, since a bare "?"
 * explains nothing. `plain` falls back to a native `title` where the mark sits inside
 * something that already carries a tooltip or a popover of its own (a calendar block, a
 * reminder chip): two tooltips would open at once there.
 */
export function UnsureNameMark({
  className,
  plain,
}: {
  className?: string;
  plain?: boolean;
}) {
  const { t } = useTranslation("customers");
  const label = t("unverified.nameUnsure");
  const mark = (
    <span
      // A bare "?" reads as nothing: announced by its label instead.
      role="img"
      title={plain ? label : undefined}
      aria-label={label}
      className={cn(
        // Raised like an exponent and kept out of the line's height, so it never pushes
        // a row taller than the name it follows.
        "ml-0.5 cursor-help align-super text-[0.7em] leading-[0] font-semibold text-muted-foreground/60",
        className,
      )}
    >
      ?
    </span>
  );
  return plain ? mark : <Tooltip content={label}>{mark}</Tooltip>;
}

/**
 * `text` with the mark right after `unsureName`, wherever the name sits in it — a calendar
 * event's title is written once, at booking ("Amina Diallo (Coupe femme)"), so the name
 * is a part of it, not a field. A text that does not contain the name gets no mark: it
 * would land on whatever the title says instead.
 */
export function TextWithUnsureName({
  text,
  unsureName,
  plain,
}: {
  text: string;
  unsureName?: string;
  plain?: boolean;
}) {
  const at = unsureName ? text.indexOf(unsureName) : -1;
  if (!unsureName || at < 0) return <>{text}</>;
  const end = at + unsureName.length;
  return (
    <>
      {text.slice(0, end)}
      <UnsureNameMark plain={plain} />
      {text.slice(end)}
    </>
  );
}

/**
 * A customer's name (`customerDisplayName`) with its "?" when it was only heard, or
 * `fallback` when the record has no name at all.
 */
export function CustomerNameText({
  customer,
  fallback,
  markClassName,
}: {
  /** Only its name fields are read: a full record or a list's summary will do. */
  customer?: Partial<
    Pick<
      Customer,
      "given_name" | "family_name" | "display_name" | "unverified_infos"
    >
  > | null;
  fallback: string;
  markClassName?: string;
}) {
  const name = customerDisplayName(customer);
  return (
    <>
      {name || fallback}
      {name && isNameUnsure(customer) && (
        <UnsureNameMark className={markClassName} />
      )}
    </>
  );
}
